import { useMantineTheme } from '@mantine/core';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ContainerWidthContext = createContext<number>(Infinity);

type ResponsiveValue<T> = Partial<Record<'base' | 'sm' | 'md' | 'lg' | 'xl', T>>;

const ORDERED_KEYS = ['xl', 'lg', 'md', 'sm'] as const;

function parseBreakpoints(breakpoints: Record<string, string>) {
  return {
    sm: parseFloat(breakpoints.sm) * 16,
    md: parseFloat(breakpoints.md) * 16,
    lg: parseFloat(breakpoints.lg) * 16,
    xl: parseFloat(breakpoints.xl) * 16,
  };
}

export function ContainerBreakpointProvider({ children }: { children: ReactNode }) {
  const [width, setWidth] = useState(Infinity);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const newWidth = Math.round(entry.contentRect.width);
      setWidth(prev => (prev === newWidth ? prev : newWidth));
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <ContainerWidthContext.Provider value={width}>
      <div ref={ref} style={{ minWidth: 0, height: '100%' }}>
        {children}
      </div>
    </ContainerWidthContext.Provider>
  );
}

export function useContainerQuery() {
  const containerWidth = useContext(ContainerWidthContext);
  const theme = useMantineTheme();
  const breakpoints = useMemo(() => parseBreakpoints(theme.breakpoints), [theme.breakpoints]);

  return function cq<T>(responsive: ResponsiveValue<T>): T {
    for (const key of ORDERED_KEYS) {
      if (key in responsive && containerWidth >= breakpoints[key]) {
        return responsive[key] as T;
      }
    }
    return responsive.base as T;
  };
}
